import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Store, TripWithItems } from '@sp/shared'
import { HttpError } from '../lib/http.ts'
import type { TripSync } from '../lib/tripSync.ts'
import { errorMessage } from '../lib/useResource.ts'
import { Sheet } from './Sheet.tsx'
import { Button, Divider, Label, MenuItem, Paragraph, TextField } from './ui.tsx'
import { RouteEditor } from './RouteEditor.tsx'
import { ConfirmSheet } from './ConfirmSheet.tsx'

/**
 * Nastaveni nakupu (navrh v3): nazev a datum se ulozi hned po opusteni pole,
 * obchody maji jeden editor pro jeden i vic obchodu. Dve a vic obchodu = trasa.
 */
export function TripMenuSheet({
  open,
  onClose,
  trip,
  stores,
  sync,
  onBackToPlanning,
  onDeleted,
}: {
  open: boolean
  onClose(): void
  trip: TripWithItems
  stores: readonly Store[]
  sync: TripSync
  /** Pri nakupovani: zpet do stavu planovani (mnozstvi, presouvani, odhad). */
  onBackToPlanning?: (() => void) | undefined
  onDeleted(): void
}) {
  const navigate = useNavigate()
  const [date, setDate] = useState(trip.plannedDate ?? '')
  const [title, setTitle] = useState(trip.title ?? '')
  const [selected, setSelected] = useState<string[]>(trip.storeIds)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!open) return
    setDate(trip.plannedDate ?? '')
    setTitle(trip.title ?? '')
    setSelected(trip.storeIds)
    setError(null)
    setSaved(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function run(action: () => Promise<void>, done: string | null, close = false) {
    setBusy(true)
    setError(null)
    setSaved(null)
    try {
      await action()
      setSaved(done)
      if (close) onClose()
    } catch (err) {
      setError(err instanceof HttpError && err.isOffline ? 'Tohle jde změnit jen se signálem.' : errorMessage(err))
      // Chyba mazani se ukaze v hlavnim panelu, potvrzeni ji neumi.
      setDeleting(false)
    } finally {
      setBusy(false)
    }
  }

  function saveTitle() {
    const next = title.trim() || null
    if (next === (trip.title?.trim() || null)) return
    void run(() => sync.updateTrip({ title: next }), 'Název uložen')
  }

  /** Az po opusteni pole: Chrome posila zmenu uz behem psani roku (0002-…, 0020-…). */
  function saveDate() {
    if (date === (trip.plannedDate ?? '')) return
    if (date && Number(date.slice(0, 4)) < 2000) return setError('Zkontroluj rok v datu')
    void run(() => sync.updateTrip({ plannedDate: date || null }), date ? 'Datum uloženo' : 'Datum smazáno')
  }

  const routeChanged = selected.join() !== trip.storeIds.join()

  return (
    <>
      <Sheet open={open && !deleting} onClose={onClose} title="Nákup" subtitle="Nastavení">
        <div className="stack">
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault()
              saveTitle()
            }}
          >
            <TextField
              label="Název"
              placeholder="Třeba Grilovačka u Pepy"
              hint="Nepovinné. Bez názvu se nákup jmenuje podle obchodu nebo dne."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={saveTitle}
              maxLength={80}
              enterKeyHint="done"
            />
            <TextField label="Kdy" type="date" value={date} onChange={(e) => setDate(e.target.value)} onBlur={saveDate} />
          </form>
          <div className="stack-4">
            <Label>{selected.length > 1 ? 'Obchody na trase' : 'Obchod'}</Label>
            <Paragraph size="meta">
              Žádný obchod = nakoupíš kdekoli. S víc obchody je to trasa – pořadí platí jen pro tenhle nákup.
            </Paragraph>
          </div>
          <RouteEditor stores={stores} selected={selected} onChange={setSelected} />
          {routeChanged && (
            <div className="button-row">
              <Button compact onClick={() => setSelected(trip.storeIds)}>
                Zrušit
              </Button>
              <Button
                compact
                kind="primary"
                disabled={busy}
                onClick={() => void run(() => sync.updateTrip({ storeIds: selected }), 'Obchody uloženy')}
              >
                Uložit obchody
              </Button>
            </div>
          )}
          {saved && <Paragraph size="meta" tone="fg">{saved}</Paragraph>}
          {error && <Paragraph tone="error">{error}</Paragraph>}
          <Divider />
          <div className="menu">
            {onBackToPlanning && (
              <MenuItem
                icon="pencil"
                iconTone="muted"
                label="Zpět k plánování"
                onClick={() => {
                  onClose()
                  onBackToPlanning()
                }}
              />
            )}
            <MenuItem
              icon="printer"
              iconTone="muted"
              label="Vytisknout seznam"
              onClick={() => {
                onClose()
                navigate(`/nakup/${trip.id}/tisk`)
              }}
            />
            {trip.status === 'done' && (
              <MenuItem
                icon="refresh"
                iconTone="muted"
                label="Vrátit mezi rozpracované"
                disabled={busy}
                onClick={() => {
                  sync.setStatus('shopping')
                  onClose()
                }}
              />
            )}
            <MenuItem icon="trash" tone="error" label="Smazat nákup" disabled={busy} onClick={() => setDeleting(true)} />
          </div>
        </div>
      </Sheet>
      <ConfirmSheet
        open={open && deleting}
        title="Smazat nákup?"
        confirmLabel="Smazat nákup"
        danger
        busy={busy}
        onClose={() => setDeleting(false)}
        onConfirm={() =>
          void run(async () => {
            await sync.deleteTrip()
            setDeleting(false)
            onDeleted()
          }, null)
        }
      >
        Smaže se i se všemi položkami a nejde to vrátit.
      </ConfirmSheet>
    </>
  )
}
